/**
 * La fiche publique d'un événement : eventmatch.pro/evenements/<slug>.
 *
 * Tout ce qu'un visiteur — et Google — doit trouver : dates, lieu et carte,
 * organisateur, description, billets, qui y sera. « J'y participe » demande un
 * compte : c'est le but, et c'est dit franchement. Les métadonnées de la page
 * (titre, JSON-LD) sont posées côté serveur ; ici on affiche.
 */
import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowUpRight, Building2, Calendar, Check, ChevronDown, MapPin, Mic2, Ticket, Users } from 'lucide-react';
import { appelerApi, envoyer } from '../reseau.ts';
import { useLibelles } from '../i18n.tsx';
import { Marque } from '../composants/Marque.tsx';
import { libelleCategorie, libelleType, type Espace, type FicheEvenement, type SessionProgramme } from './compte/types.ts';

export function EvenementPublic({ slug, rejoindreAuto }: { slug: string; rejoindreAuto: boolean }) {
  const { t, langue } = useLibelles();
  const [fiche, setFiche] = useState<FicheEvenement | null | undefined>(undefined);
  const [compte, setCompte] = useState<Espace | null | undefined>(undefined);
  const [etat, setEtat] = useState<'repos' | 'envoi' | 'inscrit'>('repos');
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    void appelerApi<FicheEvenement>(`/api/evenements/${encodeURIComponent(slug)}`).then((r) => setFiche(r.donnees ?? null));
    void appelerApi<Espace>('/api/compte/moi').then((r) => setCompte(r.donnees ?? null));
  }, [slug]);

  const dejaInscrit = Boolean(compte && fiche && compte.participations.some((p) => p.evenement_id === fiche.evenement.id));

  const participer = async () => {
    if (!fiche) return;
    if (!compte) {
      location.assign(`/compte/creer?evenement=${encodeURIComponent(slug)}`);
      return;
    }
    setEtat('envoi');
    setErreur(null);
    const r = await envoyer(`/api/compte/evenements/${fiche.evenement.id}/rejoindre`, {});
    if (r.erreur) {
      setEtat('repos');
      setErreur(r.erreur);
      return;
    }
    setEtat('inscrit');
    // On arrive sur les murs : c'est là que l'événement se joue.
    setTimeout(() => location.assign(`/mur/${slug}`), 900);
  };

  // Retour d'inscription (?rejoindre=1) : on s'inscrit dès que le compte est là.
  useEffect(() => {
    if (rejoindreAuto && compte && fiche && !dejaInscrit && etat === 'repos') void participer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rejoindreAuto, compte, fiche]);

  if (fiche === undefined) return <p className="min-h-screen bg-void p-10 text-fumee">{t('app.chargement', 'Chargement…')}</p>;
  if (fiche === null) return <Navigate to="/" replace />;

  const e = fiche.evenement;
  const locale = langue === 'nl' ? 'nl-BE' : langue === 'en' ? 'en-GB' : 'fr-BE';
  const jour = (iso: string) => new Date(iso).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Brussels' });
  const heure = (iso: string) => new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Brussels' });
  const memeJour = new Date(e.debut_le).toDateString() === new Date(e.fin_le).toDateString();
  const adresse = [e.adresse, [e.code_postal, e.lieu_ville].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const carte =
    e.lieu_lat !== null && e.lieu_lon !== null
      ? `https://www.openstreetmap.org/export/embed.html?bbox=${e.lieu_lon - 0.012},${e.lieu_lat - 0.007},${e.lieu_lon + 0.012},${e.lieu_lat + 0.007}&layer=mapnik&marker=${e.lieu_lat},${e.lieu_lon}`
      : null;
  const itineraire =
    e.lieu_lat !== null && e.lieu_lon !== null
      ? `https://www.openstreetmap.org/?mlat=${e.lieu_lat}&mlon=${e.lieu_lon}#map=16/${e.lieu_lat}/${e.lieu_lon}`
      : adresse
        ? `https://www.openstreetmap.org/search?query=${encodeURIComponent(adresse)}`
        : null;

  return (
    <div className="min-h-screen bg-void font-sans text-blanc">
      <header className="mx-auto flex max-w-[1080px] items-center justify-between px-5 py-5">
        <a href="/" className="flex items-center gap-2.5">
          <Marque taille={20} />
          <span className="text-[15px] font-medium">EventMatch<span className="text-fumee">.pro</span></span>
        </a>
        <nav className="flex items-center gap-4 text-[13px] text-fumee">
          <a href="/#agenda" className="hover:text-blanc">{t('evenement.tous', 'Tous les événements')}</a>
          <a href="/compte" className="btn-creme !min-h-0 px-4 py-1.5 text-[13px]">{t('vitrine.nav.compte', 'Mon compte')}</a>
        </nav>
      </header>

      {e.image_url && (
        <div className="mx-auto max-w-[1080px] px-5">
          <div className="relative h-[220px] overflow-hidden rounded-[14px] sm:h-[360px]">
            <img src={e.image_url} alt={e.nom} className="h-full w-full object-cover" width={1920} height={1080} />
            <div className="absolute inset-0 bg-gradient-to-t from-void via-void/20 to-transparent" />
          </div>
        </div>
      )}

      <main className="mx-auto grid max-w-[1080px] gap-8 px-5 pb-24 pt-8 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <article>
          <div className="flex flex-wrap items-center gap-2 text-[12px]">
            <span className="rounded-full border border-graphite px-3 py-1 text-fumee">{libelleCategorie(e.categorie, e.sous_categorie)}</span>
            {e.aujourdhui && <span className="bg-match-gradient rounded-full px-3 py-1 font-medium text-void">{t('vitrine.jour.badge', "Aujourd'hui")}</span>}
            {e.passe && <span className="rounded-full border border-graphite px-3 py-1 text-fumee">{t('evenement.passe', 'Événement passé')}</span>}
            {e.distance_km !== null && !e.passe && <span className="text-fumee">{t('evenement.a_km', 'à {km} km').replace('{km}', String(e.distance_km))}</span>}
          </div>

          <h1 className="display-title mt-4 text-[32px] leading-tight sm:text-[44px]">{e.nom}</h1>

          <p className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-[15px] text-blanc/90">
            <span className="inline-flex items-center gap-2"><Calendar size={16} strokeWidth={1.5} className="text-fumee" />
              <span className="capitalize">{jour(e.debut_le)}</span>
              {memeJour ? ` · ${heure(e.debut_le)} – ${heure(e.fin_le)}` : ` → ${jour(e.fin_le)}`}
            </span>
            {(e.lieu_nom || e.lieu_ville) && (
              <span className="inline-flex items-center gap-2"><MapPin size={16} strokeWidth={1.5} className="text-fumee" />{[e.lieu_nom, e.lieu_ville].filter(Boolean).join(', ')}</span>
            )}
            {e.participants > 0 && (
              <span className="inline-flex items-center gap-2"><Users size={16} strokeWidth={1.5} className="text-fumee" />{e.participants} {t('evenement.inscrits', 'inscrits')}</span>
            )}
          </p>

          {e.description && (
            <div className="mt-6 max-w-[680px] text-[15px] leading-relaxed text-blanc/85">
              {e.description.split(/\n{2,}/).map((p, i) => <p key={i} className="mb-3">{p}</p>)}
            </div>
          )}

          {fiche.programme.length > 0 && (
            <section className="mt-8">
              <p className="label-caps text-fumee">{t('evenement.programme', 'Programme')}</p>
              <p className="mt-1 text-[12px] text-fumee">{t('evenement.programme_reserve', 'Sous réserve de modification de dernière minute.')}</p>
              <Programme sessions={fiche.programme} heure={heure} />
            </section>
          )}

          {fiche.entreprises.length > 0 && (
            <section className="mt-8">
              <p className="label-caps text-fumee">{t('evenement.entreprises', 'Entreprises présentes')}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {fiche.entreprises.map((x) => (
                  <span key={x.personne_id} className="inline-flex items-center gap-2 rounded-full border border-graphite px-3 py-1.5 text-[13px]">
                    {x.logo_url ? <img src={x.logo_url} alt="" className="h-4 w-4 rounded-sm object-contain" /> : <Building2 size={13} strokeWidth={1.5} className="text-fumee" />}
                    {x.entreprise}
                    {x.secteur && <span className="text-fumee">· {x.secteur}</span>}
                  </span>
                ))}
              </div>
            </section>
          )}
        </article>

        <aside className="flex flex-col gap-4">
          <div className="rounded-[14px] border border-graphite bg-charbon p-5">
            {etat === 'inscrit' || dejaInscrit ? (
              <>
                <p className="flex items-center gap-2 text-[16px]"><Check size={16} strokeWidth={1.5} /> {t('evenement.vous_participez', 'Vous y participez')}</p>
                <p className="mt-2 text-[13px] leading-relaxed text-fumee">{t('evenement.participez_texte', 'Votre profil est prêt : retrouvez qui y sera et prenez rendez-vous avant le jour J.')}</p>
                <Link to="/compte/rendez-vous" className="btn-creme mt-4 inline-block !min-h-0 px-5 py-2 text-[14px]">{t('evenement.qui_y_sera', 'Voir qui y sera')}</Link>
              </>
            ) : e.passe ? (
              <p className="text-[14px] leading-relaxed text-fumee">{t('evenement.termine', 'Cet événement est terminé. Découvrez les prochains dans l’agenda.')}</p>
            ) : (
              <>
                <p className="text-[16px]">{t('evenement.j_y_participe', 'J’y participe')}</p>
                <p className="mt-2 text-[13px] leading-relaxed text-fumee">
                  {compte
                    ? t('evenement.participer_connecte', 'Un clic : votre profil est déjà rempli, et vous verrez qui y sera.')
                    : t('evenement.participer_compte', 'Dites-le en créant votre compte EventMatch (une minute) : vous verrez qui y sera, et les bons interlocuteurs vous trouveront.')}
                </p>
                <button onClick={() => void participer()} disabled={etat === 'envoi'} className="btn-creme mt-4 !min-h-0 px-5 py-2 text-[14px] disabled:opacity-50">
                  {etat === 'envoi' ? '…' : compte ? t('evenement.confirmer_participation', 'Je participe') : t('evenement.creer_compte_participer', 'Créer mon compte et participer')}
                </button>
                {erreur && <p className="mt-2 text-[13px]" style={{ color: 'var(--em-erreur)' }}>{erreur}</p>}
              </>
            )}
            {fiche.url_billetterie && !e.passe && (
              <a href={fiche.url_billetterie} target={e.lien_billetterie ? '_blank' : undefined} rel="noreferrer" className="mt-4 flex items-center justify-between gap-2 rounded-[8px] border border-graphite px-4 py-2.5 text-[14px] hover:bg-[#262626]">
                <span className="inline-flex items-center gap-2"><Ticket size={15} strokeWidth={1.5} /> {t('evenement.billets', 'Réserver mon billet')}</span>
                <ArrowUpRight size={14} strokeWidth={1.5} className="text-fumee" />
              </a>
            )}
          </div>

          <div className="rounded-[14px] border border-graphite bg-charbon p-5">
            <p className="label-caps text-fumee">{t('evenement.lieu', 'Lieu')}</p>
            <p className="mt-2 text-[15px]">{e.lieu_nom ?? e.lieu_ville}</p>
            {adresse && <p className="mt-1 text-[13px] text-fumee">{adresse}</p>}
            {carte && (
              <iframe title={t('evenement.carte', 'Carte')} src={carte} loading="lazy" className="mt-3 h-[200px] w-full rounded-[8px] border-0" />
            )}
            {itineraire && (
              <a href={itineraire} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-[13px] underline">{t('evenement.itineraire', 'Itinéraire')} <ArrowUpRight size={12} strokeWidth={1.5} /></a>
            )}
          </div>

          <div className="rounded-[14px] border border-graphite bg-charbon p-5">
            <p className="label-caps text-fumee">{t('evenement.organisateur', 'Organisateur')}</p>
            <div className="mt-2 flex items-center gap-3">
              {e.organisation_logo && <img src={e.organisation_logo} alt="" className="h-10 w-10 rounded-[6px] object-contain" />}
              <div className="min-w-0">
                <p className="text-[15px]">{e.organisation_nom}</p>
                {e.organisation_description && <p className="mt-1 text-[13px] leading-relaxed text-fumee">{e.organisation_description}</p>}
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-[13px]">
              {e.organisation_site && <a href={e.organisation_site} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">{t('evenement.site_organisateur', 'Site de l’organisateur')} <ArrowUpRight size={12} strokeWidth={1.5} /></a>}
              {e.site_web && <a href={e.site_web} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">{t('evenement.site_evenement', 'Site de l’événement')} <ArrowUpRight size={12} strokeWidth={1.5} /></a>}
            </div>
          </div>
        </aside>
      </main>

      <footer className="mx-auto max-w-[1080px] px-5 pb-10 text-[12px] text-fumee">
        {t('page_perso.pied', 'EventMatch.pro — le réseau des entrepreneurs qui se rencontrent utilement. Offert par Elite Lead.')}
      </footer>
    </div>
  );
}

/** Le programme comme sur l'affiche : un créneau, ses activités par salle ; une ligne s'ouvre sur le détail. */
function Programme({ sessions, heure }: { sessions: SessionProgramme[]; heure: (iso: string) => string }) {
  const [ouverte, setOuverte] = useState<string | null>(null);
  const creneaux = new Map<string, SessionProgramme[]>();
  for (const s of sessions) creneaux.set(s.debut_le, [...(creneaux.get(s.debut_le) ?? []), s]);
  return (
    <div className="mt-3 flex flex-col">
      {[...creneaux.entries()].map(([debut, liste]) => (
        <div key={debut} className="flex gap-4 border-b border-graphite py-3 last:border-0">
          <span className="w-14 shrink-0 pt-0.5 font-serif text-[18px] text-blanc">{heure(debut)}</span>
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            {liste.map((s) => {
              const detail = ouverte === s.id;
              return (
                <div key={s.id}>
                  <button onClick={() => setOuverte(detail ? null : s.id)} className="flex w-full items-start justify-between gap-3 text-left">
                    <span className="min-w-0">
                      <span className="block text-[14px] text-blanc">
                        {s.salle && <span className="text-fumee">{s.salle} · </span>}
                        {s.entreprise && <span className="text-fumee">{s.entreprise} — </span>}
                        {s.titre}
                      </span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-x-3 text-[12px] text-fumee">
                        <span>{libelleType(s.type)}</span>
                        {s.intervenants.length > 0 && (
                          <span className="inline-flex items-center gap-1"><Mic2 size={11} strokeWidth={1.5} />{s.intervenants.map((i) => i.nom).join(', ')}</span>
                        )}
                      </span>
                    </span>
                    {(s.description || s.intervenants.length > 0 || s.lien) && (
                      <ChevronDown size={14} strokeWidth={1.5} className={`mt-1 shrink-0 text-fumee transition-transform ${detail ? 'rotate-180' : ''}`} />
                    )}
                  </button>
                  {detail && (
                    <div className="mt-2 rounded-[8px] bg-charbon p-3 text-[13px] leading-relaxed text-fumee">
                      <p className="text-blanc/80">{heure(s.debut_le)} – {heure(s.fin_le)}{s.salle ? ` · ${s.salle}` : ''}</p>
                      {s.description && <p className="mt-1.5">{s.description}</p>}
                      {s.intervenants.length > 0 && (
                        <ul className="mt-2 flex flex-col gap-0.5">
                          {s.intervenants.map((i) => (
                            <li key={i.nom}><span className="text-blanc">{i.nom}</span>{i.entreprise ? ` — ${i.entreprise}` : ''}{i.role ? `, ${i.role}` : ''}</li>
                          ))}
                        </ul>
                      )}
                      {s.lien && <a href={s.lien} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-blanc underline">En savoir plus <ArrowUpRight size={11} strokeWidth={1.5} /></a>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
